import { Component, Input, HostBinding, SimpleChanges, OnChanges, ChangeDetectorRef, OnInit, EventEmitter, Output } from '@angular/core';

import * as moment from 'moment-timezone';
import { TranslateService } from '@ngx-translate/core';
import { UnitsPipe } from 'app/shared/pipes/units.pipe';
import { AssetListItem } from 'app/services';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { getIconSvgString, MAP_COLORS, MAP_COLORS_ENUM } from 'app/shared/components/map/map.markers';
import { AppService } from 'app/app.service';
import { BaseComponent } from 'app/shared/components';
import { interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface SummaryMovement {
    date: string;
    active: boolean;
    location: string;
    speed: number;
    speedLimit: number;
    lastMovement: string;
    lastTripEnd: string;
    assetType: string;
}

export interface SummaryTrip {
    distance: number;
    dateStart: string;
    addressStart: string;
}

@Component({
    selector: 'key-details-overview-summary',
    templateUrl: 'details-overview-summary.component.html',
    styleUrls: ['details-overview-summary.component.scss'],
})
export class KeyDetailsOverviewSummaryComponent extends BaseComponent implements OnInit, OnChanges {

    @Input() asset: AssetListItem;
    @Input() movement: SummaryMovement;
    @Input() trip: SummaryTrip;
    @Input() allowEditing: boolean;

    @Output() edit = new EventEmitter<AssetListItem>();

    duration: {
        ago: string;
        seconds: number;
        since: string;
    };

    @HostBinding('class.details-overview__summary') summaryClass = true;

    constructor(
        private app: AppService,
        private i18n: TranslateService,
        private sanitizer: DomSanitizer,
        private units: UnitsPipe,
        private ref: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit() {

        interval(10e3)
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                this.duration = this.getDuration();
                this.ref.markForCheck();
            });

    }

    ngOnChanges(_changes: SimpleChanges) {
        this.duration = this.getDuration();
    }

    getDuration() {
        if (this.asset.telemetry) {
            let duration: string = null;
            if (this.asset.telemetry.active) {
                // if we're in a trip, the duration is from the start of the trip, or the last time it moved
                duration = this.trip?.dateStart || this.movement?.lastMovement || this.movement?.date;
            } else {
                duration = this.movement?.lastTripEnd;
            }
            if (duration) {
                return {
                    ago: moment.utc(this.asset.telemetry?.date).tz(moment.defaultZone.name).fromNow(),
                    seconds: duration ? moment.duration(moment().diff(moment(duration))).asSeconds() : null,
                    since: duration,
                };
            }
        }
        return null;
    }

    getMovementText(data: SummaryMovement): string {
        // TODO: implement better check for static assets when it gets implemented on api client
        // the api should return assetType object which whould have a features object that contains a static check
        if (!data || data.assetType === 'Fixed Asset' || data.assetType === 'Container') { return null; }

        if (data.active) {
            if (data.location) {

                if (data.speed > 0) {
                    return this.i18n.instant('STATUS.DETAILS.SUMMARY.MOVING_AT_SPEED_LOCATION', {
                        speed: this.units.transform(data.speed, 'speed'),
                        location: data.location,
                    });
                } else {
                    if (data.lastMovement) {
                        return this.i18n.instant('STATUS.DETAILS.SUMMARY.STOPPED_AT_LOCATION_SINCE_TIME', {
                            time: this.units.transform(data.lastMovement, 'time'),
                            location: data.location,
                        });
                    } else {
                        return this.i18n.instant('STATUS.DETAILS.SUMMARY.STOPPED_AT_LOCATION', {
                            location: data.location,
                        });
                    }
                }
            } else {
                return this.i18n.instant('STATUS.DETAILS.SUMMARY.POSITION_UNKNOWN_AS_NO_GPS_INFORMATION_IS_AVAILABLE');
            }
        } else {
            if (data.lastMovement) {
                return this.i18n.instant(this.isToday(data.lastMovement) ? 'STATUS.DETAILS.SUMMARY.STOPPED_AT_LOCATION' : 'STATUS.DETAILS.SUMMARY.PARKED_AT_LOCATION_SINCE_TIME_ON_DATE', {
                    location: data.location,
                    time: this.units.transform(data.lastMovement, 'time'),
                    date: this.units.transform(data.lastMovement, 'date'),
                });
            } else {
                if (data.location) {
                    return this.i18n.instant('STATUS.DETAILS.SUMMARY.PARKED_AT_LOCATION', { location: data.location });
                } else {
                    return this.i18n.instant('STATUS.DETAILS.SUMMARY.POSITION_UNKNOWN_AS_NO_GPS_INFORMATION_IS_AVAILABLE');
                }
            }
        }
    }

    isToday(date: string) {
        return moment.utc(date, 'YYYY-MM-dd HH:mm:ss').tz(moment.defaultZone?.name).isSame(moment(), 'day');
    }


    sanitize(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    getMapIcon(asset: AssetListItem, size: number): string {

        if (!asset.telemetry) {
            return '';
        }

        let iconType = 'idle';
        if (asset.telemetry.active) {
            if (!!asset.telemetry.location && asset.telemetry.location.speed === 0) {
                iconType = 'stop';
            } else {
                iconType = 'direction';
            }
        }

        return getIconSvgString(this.app.theme, iconType, {
            fill: ((typeof asset.color === 'number')
                ? MAP_COLORS[asset.color || 0]
                : MAP_COLORS[MAP_COLORS_ENUM[asset.color] || 0]),
            rotation: asset.telemetry.location && asset.telemetry.location.heading || 0,
            size: size,
            dropShadowAlpha: 0,
        });
    }

    async editAsset() {
        if (this.allowEditing) {
            this.edit.emit(this.asset);
        }
    }

}
